
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CreateSundayMarketPriceInput, SundayMarketPrice } from '@/types/database/sunday-market-prices';
import { useCreateSundayMarketPrice, useUpdateSundayMarketPrice } from '@/hooks/use-sunday-market-prices';

const formSchema = z.object({
  product_name: z.string().min(1, 'Product name is required'),
  price: z.number().min(0, 'Price must be non-negative'),
  location: z.string().min(1, 'Location is required'),
  product_remark: z.string().optional(),
  date: z.string().min(1, 'Date is required'),
});

interface SundayMarketPriceFormProps {
  initialData?: SundayMarketPrice | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const SundayMarketPriceForm: React.FC<SundayMarketPriceFormProps> = ({ 
  initialData, 
  onSuccess, 
  onCancel 
}) => {
  const createMutation = useCreateSundayMarketPrice();
  const updateMutation = useUpdateSundayMarketPrice();
  const isEditing = !!initialData;

  const form = useForm<CreateSundayMarketPriceInput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      product_name: initialData?.product_name || '',
      price: initialData?.price || 0,
      location: initialData?.location || '',
      product_remark: initialData?.product_remark || '',
      date: initialData?.date || new Date().toISOString().split('T')[0],
    },
  });

  const onSubmit = async (data: CreateSundayMarketPriceInput) => {
    try {
      if (isEditing && initialData) {
        await updateMutation.mutateAsync({ id: initialData.id, price: data });
      } else {
        await createMutation.mutateAsync(data);
      }
      form.reset();
      onSuccess();
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleCancel = () => {
    form.reset();
    onCancel();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="product_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Product Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter product name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Price (ETB)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="0.00"
                    step="0.01"
                    {...field}
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Location</FormLabel>
              <FormControl>
                <Input placeholder="Enter location" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="product_remark"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Product Remark</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Enter any remarks about the product" 
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {isEditing ? 'Update' : 'Add'} Price
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default SundayMarketPriceForm;
